import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { AUDIT_TOTALS, LATEST_REPORTS } from '../../data/auditReportsSummary';
import { SEVERITIES, SeverityBar, SeverityChip, statusClass } from '../severity';

/*
 * Homepage strip for the audit portfolio. Imports the SUMMARY data file on purpose — the full
 * report list with every issue title lives in src/data/auditReports.js and would otherwise be
 * pulled into the homepage bundle. Numbers are generated from the published PDFs.
 */

const AuditHighlights = () => (
  <section className="relative overflow-hidden border-t border-white/[0.06] bg-black py-24 md:py-32" aria-labelledby="audit-portfolio-heading">
    <div
      className="pointer-events-none absolute left-1/2 top-0 h-[380px] w-[min(1100px,100vw)] -translate-x-1/2 bg-[radial-gradient(50%_60%_at_50%_0%,rgba(0,169,158,0.08),transparent_70%)]"
      aria-hidden="true"
    />

    <div className="relative mx-auto max-w-6xl px-6">
      <div className="mb-12 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
        <div className="max-w-2xl">
          <p className="mb-5 inline-flex items-center gap-2.5 text-sm font-semibold text-teal-300">
            <span className="h-1.5 w-1.5 rounded-full bg-[#00A99E] shadow-[0_0_0_4px_rgba(0,169,158,0.18)]" aria-hidden="true" />
            Audit portfolio
          </p>
          <h2 id="audit-portfolio-heading" className="text-4xl font-semibold leading-[1.08] tracking-[-0.03em] text-white md:text-5xl">
            {AUDIT_TOTALS.reports} published reports,{' '}
            <span className="bg-gradient-to-r from-teal-300 via-cyan-300 to-indigo-300 bg-clip-text text-transparent">
              {AUDIT_TOTALS.findings} findings in the open.
            </span>
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-[#a3acb9]">
            Severity, resolution status and every issue title, taken straight from the reports themselves.
          </p>
        </div>
        <Link
          to="/reports"
          className="group inline-flex shrink-0 items-center gap-1.5 self-start rounded-full px-5 py-2.5 text-sm font-semibold text-[#e6ebf1] ring-1 ring-white/15 transition-colors hover:bg-white/[0.06] hover:ring-white/30 md:self-auto"
        >
          All reports
          <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </Link>
      </div>

      {/* Portfolio-wide distribution */}
      <div className="mb-6 rounded-2xl bg-[#0a0f16] p-6 ring-1 ring-white/[0.08]">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-[#e6ebf1]">Findings by severity</h3>
          <p className="text-xs text-[#8792a2]">
            {AUDIT_TOTALS.status.Resolved || 0} resolved · {AUDIT_TOTALS.status.Acknowledged || 0} acknowledged
          </p>
        </div>
        <SeverityBar counts={AUDIT_TOTALS.severity} total={AUDIT_TOTALS.findings} height="h-3" />
        <ul className="mt-4 flex flex-wrap gap-2">
          {SEVERITIES.filter((s) => AUDIT_TOTALS.severity[s.key] > 0).map((s) => (
            <li key={s.key}>
              <SeverityChip sev={s} count={AUDIT_TOTALS.severity[s.key]} />
            </li>
          ))}
        </ul>
      </div>

      {/* Latest reports */}
      <ul className="grid gap-3 md:grid-cols-2">
        {LATEST_REPORTS.map((r) => (
          <li key={r.id}>
            <a
              href={r.pdf}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex h-full flex-col gap-4 rounded-2xl bg-[#0a0f16] p-5 ring-1 ring-white/[0.08] transition-all duration-300 hover:-translate-y-0.5 hover:ring-white/20"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="truncate text-base font-semibold text-white">{r.client}</h3>
                  <p className="truncate text-sm text-[#a3acb9]">{r.scope}</p>
                </div>
                <span className="shrink-0 text-xs text-[#8792a2]">{r.date}</span>
              </div>

              <div className="mt-auto">
                <SeverityBar counts={r.severity} total={r.total} />
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  {SEVERITIES.filter((s) => r.severity[s.key] > 0).map((s) => (
                    <SeverityChip key={s.key} sev={s} count={r.severity[s.key]} />
                  ))}
                  {r.status.Resolved ? (
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${statusClass('Resolved')}`}>
                      {r.status.Resolved} resolved
                    </span>
                  ) : null}
                </div>
              </div>
            </a>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

export default AuditHighlights;
